<template>
  <p class="hidden">StockStatus</p>
  <div class="StockStatus flex items-center">
    <div
      class="StockStatus rounded-full w-8 h-8 mr-8 shrink-0"
      :class="[ `bg-${stockLabel.color}`, `border-${stockLabel.borderColor}`, {
        'border': stockLabel.borderColor !== ''
      } ]"
    />
    <div>
      <span
        class="type-sm"
        :class="{
          'whitespace-nowrap': useShortLabels && !props.usePrimaryStockStatus,
          'text-text-base block': isProductCard || cartItem,
          'type-xs': cartItem && !isCheckout,
        }"
        v-html="shownText"
      />
      <span
        v-if="stockLabel.type === 'soldout' && !isProductCard && productItem"
        class="type-sm pl-12"
      >
        <a
          v-if="hasRelatedProducts"
          href="#pdpRelatedProducts"
          class="underline"
        >{{ $t('stock.soldout.hasRelated') }}</a>
        <span v-if="hasRelatedProducts && globalContent.getContactUsPage">{{ $t('stock.soldout.or') }}</span>
        <NuxtLink
          v-if="globalContent.getContactUsPage"
          :to="$sbLinkFix(globalContent.getContactUsPage)"
          class="underline"
          :class="{
            'capitalize': !hasRelatedProducts
          }"
        >
          {{ $t('stock.soldout.contactSupport') }}
        </NuxtLink>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductVariantModel } from '#models/productVariant';
import useStockStatus, { StockLabel } from '~/composables/useStockStatus';
import { useGlobalContentStore } from '~/store/globalContent';
import { ProductModel } from '#models/product';
import { CartApiItem } from '#constants/types/norce';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
const globalContent = useGlobalContentStore();

interface Props {
  variant?: ProductVariantModel;
  productItem?: ProductModel;
  useShortLabels?: boolean;
  useArea?: boolean;
  isProductCard?: boolean;
  isCheckout?: boolean;
  cartItem?: CartApiItem;
  usePrimaryStockStatus?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  useShortLabels: false,
  isProductCard: false,
  isCheckout: false,
  useArea: false,
  usePrimaryStockStatus: false,
});

const { getStockLabel, getStockLabelForCartItem } = useStockStatus();

const alternativeText = computed(()=> {
  const hasCodes = props.productItem?.categories.map(m => m.code);
  return globalContent.getAltDeliveryTextCategories.filter(f => hasCodes.includes(f)).length > 0;
});

const isNewProduct = computed(() => props.productItem?.hasNewFlag || false);

const stockLabel = computed<StockLabel>(() => {
  if (props.cartItem) {
    return getStockLabelForCartItem(props.cartItem);
  }
  return getStockLabel(props.variant, true, alternativeText.value, props.usePrimaryStockStatus, isNewProduct.value);
});

const shownText = computed(()=> {
  if (props.useArea && stockLabel.value.areaText) {
    return stockLabel.value.areaText;
  }
  return props.useShortLabels && !props.usePrimaryStockStatus ? stockLabel.value.textShort : stockLabel.value.text;
});

const hasRelatedProducts = computed(()=> {
  if (!props.productItem.relatedProducts) {
    return false;
  }
  const hasRelated = props.productItem.relatedProducts.findIndex(f => f.code === NorceRelatedProductTypeCodes.Related);
  return (hasRelated > -1);
});

</script>
